import { IEditorV3 } from "@azu-business/editor-v3";
import { AioExternalSingle } from "aic";
import { DerivedSaveBlockerType, ISaveBlocker } from "./interface";

/**
 * Builds warning blockers for a list of replacements, flagging missing text, duplicates and
 * new text that contains the original text.
 *
 * @param replacements - Replacements (singles or domain replacements) to validate.
 * @param isDuplicate - Predicate determining whether two replacements would clash.
 * @param replacementType - Category type for the replacements (e.g., "singles", "domainReplacements").
 * @returns The set of blockers describing each validation failure.
 */
export const getReplacementSaveBlockers = <T extends AioExternalSingle<IEditorV3>>(
  replacements: T[],
  isDuplicate: (a: T, b: T) => boolean,
  replacementType: DerivedSaveBlockerType,
): Set<ISaveBlocker> => {
  const blockers = new Set<ISaveBlocker>();
  replacements.forEach((replacement) => {
    if (replacement.oldText === "") {
      blockers.add({
        id: replacement.airid,
        type: replacementType,
        warningText: "Missing text will be ignored",
      });
    }
    if (replacements.filter((other) => isDuplicate(replacement, other)).length > 1) {
      blockers.add({
        id: replacement.airid,
        type: replacementType,
        warningText: "Duplicates will cause unpredictable results",
      });
    }
    if (
      replacement.oldText !== "" &&
      replacement.newText.lines
        .map((l) => l.textBlocks.map((b) => b.text).join(""))
        .join("\n")
        .includes(replacement.oldText)
    ) {
      blockers.add({
        id: replacement.airid,
        type: replacementType,
        warningText: "New text cannot contain the original text",
      });
    }
  });
  return blockers;
};
